import { useState } from "react";
import DetailsCard from "@/components/DetailsCard";
import MultiSectionDetailsCard from "@/components/MultiSectionDetailsCard";
import { Person } from "@/types";
import {
  addressOptions,
  emailOptions,
  facilitySections,
  officialDetailsOptions,
  orphanedContactPreferenceOption,
  personalOptions,
  phoneOptions,
  sectionOptions
} from "../../utils/utils";
import { useCanManagePeople, useCanManageChapters, useCanEditEmail, useCanEditLastName } from "@/hooks/useCapabilities";
import PhoneEditForm from "./forms/PhoneEditForm";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import AddressEditForm from "./forms/AddressEditForm";
import { useSnackbar } from "notistack";
import PersonalEditForm from "./forms/PersonalEditForm";
import OfficialEditForm from "./forms/OfficialEditForm";
import EmailEditModal from "./modals/EmailEditModal";
import UpdatePreferencesModal from "./modals/UpdatePreferencesModal";
import SectionHeading from "@/components/SectionHeading";
import SectionChapterAffiliationsEditForm from "./forms/SectionChapterAffiliationsEditForm";

export interface UpdatedFields {
  mobilePhone?: boolean;
  homePhone?: boolean;
  email?: boolean;
  homeAddress?: boolean;
  directoryOptOut?: boolean;
}

function DetailsColumn({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-y-10">{children}</div>;
}

interface ContactDetailsProps {
  person: Person;
  updatedFields?: UpdatedFields;
  suppressInlineEditing?: boolean;
}

export function ContactDetails({ person, updatedFields, suppressInlineEditing = false }: ContactDetailsProps) {
  const canEdit = useCanManagePeople() && !suppressInlineEditing;
  const canManageChapters = useCanManageChapters() && !suppressInlineEditing;
  const canEditEmail = useCanEditEmail() && !suppressInlineEditing;
  const canEditLastName = useCanEditLastName() && !suppressInlineEditing;
  const { setError, clearError } = useErrorBanner();
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const clearEditingSection = () => setEditingSection(null);
  const [currentPerson, setCurrentPerson] = useState<Person>(person);
  const { enqueueSnackbar } = useSnackbar();
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  const [emailEditOpen, setEmailEditOpen] = useState(false);
  const cardEditingSection = emailEditOpen ? "email-modal" : editingSection;

  const handleSectionChange = (section: string | null) => {
    if (section === "Email") {
      setEmailEditOpen(true);
      return;
    }
    setEmailEditOpen(false);
    setEditingSection(section);
  };

  const closeEmailEdit = () => setEmailEditOpen(false);

  const onSuccess = (updatedPerson: Partial<Person>) => {
    clearError();
    clearEditingSection();
    closeEmailEdit();
    setCurrentPerson((prevPerson) => ({
      ...prevPerson,
      ...updatedPerson
    }));
    enqueueSnackbar("Contact details updated successfully");
  };

  const onOfficialSuccess = (updatedPerson: Partial<Person>) => {
    clearError();
    clearEditingSection();
    closeEmailEdit();
    setCurrentPerson((prevPerson) => ({
      ...prevPerson,
      ...updatedPerson
    }));
    enqueueSnackbar("Official data updated");
  };

  const onError = (error: string) => {
    clearEditingSection();
    setError(error);
  };

  const openPreferencesModal = () => {
    setIsPreferencesModalOpen(true);
  };

  const closePreferencesModal = () => {
    setIsPreferencesModalOpen(false);
  };

  const orphanedContactPreference = orphanedContactPreferenceOption(currentPerson);
  const addressSectionOptions = [
    ...addressOptions(currentPerson.addresses, currentPerson.contactAddressId),
    ...(orphanedContactPreference ? [orphanedContactPreference] : [])
  ];

  return (
    <>
      <SectionHeading
        heading="Personal and Contact Information"
        body="View contact information and other personal details below"
        actions={
          canEdit
            ? [
                {
                  label: "Update Preferences",
                  onClick: openPreferencesModal,
                  testId: "update-preferences-button"
                }
              ]
            : []
        }
      />
      <div className="flex flex-col-reverse md:flex-row gap-x-10 gap-y-10" data-testid="contact-details">
        <div id="contact-details-column-1" className="basis-1/3">
          <DetailsColumn>
            <MultiSectionDetailsCard
              editingSection={cardEditingSection}
              setEditingSection={handleSectionChange}
              sections={[
                {
                  heading: "Personal",
                  options: personalOptions(currentPerson).map((opt) => ({
                    ...opt,
                    ...(opt.overline === "PGA Directory Visibility" &&
                      updatedFields?.directoryOptOut != null && {
                        isUpdated: updatedFields.directoryOptOut
                      })
                  })),
                  ...(canEdit && {
                    renderEdit: () => (
                      <PersonalEditForm
                        personId={currentPerson.id}
                        onCancel={clearEditingSection}
                        onSuccess={onSuccess}
                        onError={onError}
                        informalName={currentPerson.informalName}
                        preferredLastName={currentPerson.preferredLastName}
                        dob={currentPerson.dob}
                        gender={currentPerson.gender}
                        ssn={currentPerson.ssn?.full}
                        spouseName={currentPerson.spouseName ?? undefined}
                      />
                    )
                  })
                },
                {
                  heading: "Official",
                  options: officialDetailsOptions(currentPerson, canEditLastName),
                  ...(canEdit && {
                    renderEdit: () => (
                      <OfficialEditForm
                        personId={currentPerson.id}
                        firstName={currentPerson.firstName}
                        lastName={currentPerson.lastName}
                        middleName={currentPerson.middleName}
                        suffixName={currentPerson.suffixName}
                        canEditLastName={canEditLastName}
                        onCancel={clearEditingSection}
                        onSuccess={onOfficialSuccess}
                        onError={onError}
                      />
                    )
                  })
                }
              ]}
            />
          </DetailsColumn>
        </div>
        <div id="contact-details-column-2" className="basis-2/3">
          <DetailsColumn>
            <MultiSectionDetailsCard
              editingSection={cardEditingSection}
              setEditingSection={handleSectionChange}
              sections={[
                {
                  heading: "Phone",
                  options: phoneOptions(currentPerson.phoneNumbers).map((opt) => ({
                    ...opt,
                    ...(opt.overline === "Mobile" &&
                      updatedFields?.mobilePhone != null && {
                        isUpdated: updatedFields.mobilePhone
                      }),
                    ...(opt.overline === "Home" &&
                      updatedFields?.homePhone != null && {
                        isUpdated: updatedFields.homePhone
                      })
                  })),
                  ...(canEdit && {
                    renderEdit: () => (
                      <PhoneEditForm
                        personId={currentPerson.id}
                        phoneNumbers={currentPerson.phoneNumbers}
                        onCancel={clearEditingSection}
                        onSuccess={onSuccess}
                        onError={onError}
                      />
                    )
                  })
                },
                {
                  heading: "Email",
                  options: emailOptions(currentPerson.email).map((opt) => ({
                    ...opt,
                    ...(updatedFields?.email != null && {
                      isUpdated: updatedFields.email
                    })
                  })),
                  ...(canEditEmail && {
                    renderEdit: () => null
                  })
                }
              ]}
            />

            <MultiSectionDetailsCard
              editingSection={cardEditingSection}
              setEditingSection={handleSectionChange}
              sections={[
                {
                  heading: "Address",
                  options: addressSectionOptions.map((opt) => ({
                    ...opt,
                    ...(opt.overline === "Home Address" &&
                      updatedFields?.homeAddress != null && {
                        isUpdated: updatedFields.homeAddress
                      })
                  })),
                  ...(canEdit && {
                    renderEdit() {
                      return (
                        <AddressEditForm
                          personId={currentPerson.id}
                          addresses={currentPerson.addresses}
                          onCancel={clearEditingSection}
                          onSuccess={onSuccess}
                          onError={onError}
                        />
                      );
                    }
                  })
                },
                ...facilitySections(currentPerson.employments, currentPerson.contactAddressId)
              ]}
            />
            {currentPerson.primarySection && (
              <DetailsCard
                heading="Section"
                options={sectionOptions(currentPerson, true)}
                editingSection={cardEditingSection}
                {...(canManageChapters && {
                  onEdit: () => handleSectionChange("Section"),
                  renderEdit: () => (
                    <SectionChapterAffiliationsEditForm
                      person={currentPerson}
                      onCancel={clearEditingSection}
                      onSuccess={onSuccess}
                      onError={onError}
                    />
                  )
                })}
              />
            )}
            {person.militaryService && (
              <DetailsCard
                heading="Military"
                options={[
                  {
                    iconName: "military_tech",
                    overline: "Status",
                    headline: person.militaryService.status
                  }
                ]}
                editingSection={cardEditingSection}
              />
            )}
          </DetailsColumn>
        </div>
      </div>
      {canEditEmail && emailEditOpen && (
        <EmailEditModal
          open={emailEditOpen}
          personId={currentPerson.id}
          email={currentPerson.email}
          identityPerson={currentPerson}
          onCancel={closeEmailEdit}
          onSuccess={onSuccess}
          onError={onError}
        />
      )}
      {canEdit && (
        <UpdatePreferencesModal
          person={currentPerson}
          open={isPreferencesModalOpen}
          onClose={closePreferencesModal}
          onSuccess={onSuccess}
        />
      )}
    </>
  );
}
